import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Newspaper } from 'lucide-react';
import { Button } from './ui/button';
import Logo from './Logo';
import SocialIcons from './SocialIcons';
import AdBanner from './AdBanner';
import { toast } from 'sonner';
import { useCategories, useDistricts } from '../hooks/useCatalog';
import { useLanguage } from '../contexts/LanguageContext';
import { useWebsiteSettings } from '../contexts/WebsiteSettingsContext';
import { localizeName } from '../i18n/translations';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const { data: allCategories = [] } = useCategories();
  const { data: districts = [] } = useDistricts();
  const categories = allCategories.slice(0, 8);
  const { lang, t } = useLanguage();
  const { settings, siteName } = useWebsiteSettings();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      toast.success(t('subscribed'));
      setEmail('');
    } catch (err) {
      toast.error('Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  if (!settings) {
    return <footer className="bg-black text-white mt-20 p-8 text-center text-white/50">{t('loading')}</footer>;
  }

  return (
    <footer className="bg-black text-white mt-20" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-4 pt-8">
        <AdBanner position="footer" className="mb-6" />
      </div>

      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 py-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-serif-kannada font-bold mb-2">{t('newsletter')}</h3>
              <p className="text-white/70">{settings.footerText}</p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('emailPlaceholder')}
                required
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 focus:outline-none focus:border-primary text-white placeholder-white/50"
                data-testid="newsletter-email-input"
              />
              <Button type="submit" disabled={subscribing} className="rounded-none bg-primary hover:bg-red-700 px-8" data-testid="newsletter-subscribe-btn">
                {subscribing ? '…' : t('subscribe')}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="mb-4 bg-white p-3 inline-block">
              <Logo size="small" />
            </div>
            <p className="text-white/70 text-sm leading-relaxed">{settings.footerText}</p>
            <p className="text-white/50 text-xs mt-2">{siteName(lang)}</p>
            <Link
              to="/epaper"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-primary text-white text-sm font-label hover:bg-red-700 transition-colors"
              data-testid="footer-epaper-btn"
            >
              <Newspaper size={16} /> {t('epaper')}
            </Link>
            <SocialIcons
              className="flex gap-3 mt-6"
              itemClassName="w-9 h-9 border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors"
              size={16}
            />
          </div>

          <div>
            <h4 className="font-label text-sm mb-4 text-primary">{t('categories')}</h4>
            <ul className="space-y-2">
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link to={`/category/${cat.slug}`} className="text-white/70 hover:text-primary transition-colors text-sm">
                    {localizeName(cat, lang)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-label text-sm mb-4 text-primary">{t('districts')}</h4>
            <ul className="space-y-2">
              {districts.map((district) => (
                <li key={`${lang}-footer-district-${district.id}`}>
                  <Link to={`/district/${district.slug}`} className="text-white/70 hover:text-primary transition-colors text-sm">
                    {localizeName(district, lang)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-label text-sm mb-4 text-primary">{t('contact')}</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                <span>{settings.orgAddress}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="flex-shrink-0" />
                <a href={`tel:${String(settings.orgPhone).replace(/\s/g, '')}`} className="hover:text-primary">{settings.orgPhone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={16} className="flex-shrink-0" />
                <a href={`mailto:${settings.orgEmail}`} className="hover:text-primary">{settings.orgEmail}</a>
              </li>
              <li>
                <Link to="/contact" className="hover:text-primary" data-testid="footer-contact-link">{t('contact')}</Link>
                {' · '}
                <Link to="/social" className="hover:text-primary" data-testid="footer-social-link">{t('connect')}</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-xs text-center md:text-left">
            {settings.copyrightText || `© 2026 ${siteName(lang)}. ${t('allRights')}`}
            {' · '}
            {settings.developedByText || t('developedBy')}{' '}
            <Link to="/developer" className="hover:text-primary underline-offset-2 hover:underline" data-testid="footer-developer-link">
              {settings.developerName}
            </Link>
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-xs text-white/50">
            <Link to="/" className="hover:text-primary">{t('home')}</Link>
            <Link to="/epaper" className="hover:text-primary">{t('epaper')}</Link>
            <Link to="/contact" className="hover:text-primary">{t('contact')}</Link>
            <Link to="/social" className="hover:text-primary">{t('connect')}</Link>
            <Link to="/developer" className="hover:text-primary">{t('developer')}</Link>
            <Link to="/search" className="hover:text-primary">{t('search')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
